import { pb } from "./pocketbase";
import { stockProductionService } from "./stockProductionService";

export const loteService = {
  // --- 1. GERAR PRÓXIMO CÓDIGO SEQUENCIAL DE LOTE (LOTE-YYYY-XXX) ---
  async getNextLoteCode() {
    const year = new Date().getFullYear();
    try {
      const records = await pb.collection("estoque_lotes").getList(1, 1, {
        filter: `codigo ~ "LOTE-${year}-"`,
        sort: "-created",
      });
      if (records.items.length === 0) {
        return `LOTE-${year}-001`;
      }
      const lastCode = records.items[0].codigo; // Ex: "LOTE-2026-041"
      const parts = lastCode.split("-");
      const lastNum = parseInt(parts[parts.length - 1]) || 0;
      const nextNum = lastNum + 1;
      const paddedNum = String(nextNum).padStart(3, "0");
      return `LOTE-${year}-${paddedNum}`;
    } catch (e) {
      console.error("[LoteService] Erro ao gerar próximo código de lote:", e);
      return `LOTE-${year}-001`;
    }
  },

  // --- 2. BUSCAR TODOS OS LOTES ---
  async getLotes(filter = "") {
    return await pb.collection("estoque_lotes").getFullList({
      filter: filter,
      sort: "-created",
      expand: "criado_por,aprovado_por",
    });
  },

  // --- 3. CRIAR LOTE RASCUNHO (Escritório ou Planejamento) ---
  async createDraftLote(itens, userId, username, observacoes = "", origem = "escritorio", status = null) {
    const codigo = await this.getNextLoteCode();
    const finalStatus = status || (origem === "planejamento" ? "aguardando_aprovacao" : "rascunho");
    const payload = {
      codigo,
      origem, // 'escritorio' ou 'planejamento'
      status: finalStatus,
      itens_planejados: itens, // [{sku, qtd}]
      items: JSON.stringify(itens), // para compatibilidade de schema
      criado_por: userId || null,
      solicitante: username || (origem === "planejamento" ? "Planejamento" : "Sistema"),
      observacoes: observacoes,
    };
    return await pb.collection("estoque_lotes").create(payload);
  },

  // --- 4. SOLICITAR LOTE PELA OFICINA (Buffer de Aprovação) ---
  async requestLoteFromOficina(itens, operatorName, motivo, observacoes = "") {
    const codigo = await this.getNextLoteCode();
    const payload = {
      codigo,
      origem: "solicitacao_oficina",
      status: "aguardando_aprovacao",
      solicitante: operatorName || "Operador",
      itens_planejados: itens, // [{sku, qtd}]
      items: JSON.stringify(itens),
      motivo_solicitacao: motivo,
      observacoes: `Solicitado por ${operatorName || 'Operador'}. ${observacoes}`,
    };
    return await pb.collection("estoque_lotes").create(payload);
  },

  // --- 5. APROVAR LOTE (Move status e cria os itens) ---
  async approveLote(loteId, gestorUser) {
    const lote = await pb.collection("estoque_lotes").getOne(loteId);
    
    // 1. Atualiza o lote para em_producao
    const updatedLote = await pb.collection("estoque_lotes").update(loteId, {
      status: "em_producao",
      aprovado_por: gestorUser?.id || null,
      data_aprovacao: new Date().toISOString(),
    });

    // 2. Dispara o launchBatch para cada item planejado
    try {
      let items = lote.items || lote.itens_planejados || lote.items_json || [];
      if (typeof items === "string") {
        try { items = JSON.parse(items); } catch(e) {}
      }
      if (Array.isArray(items)) {
        for (const item of items) {
          if (item.sku && Number(item.qtd) > 0) {
            await stockProductionService.launchBatch(item.sku, Number(item.qtd), gestorUser?.name || "Gestor", loteId);
          }
        }
      }
    } catch (e) {
      console.error("[LoteService] Erro ao criar itens do lote aprovado:", e);
      // Mantém o lote em produção, mas loga o erro.
      throw new Error("Lote aprovado, mas falhou ao lançar itens: " + e.message);
    }

    return updatedLote;
  },

  // --- 6. REJEITAR/CANCELAR LOTE ---
  async rejectLote(loteId, gestorUser, motivoRejeicao) {
    const lote = await pb.collection("estoque_lotes").getOne(loteId);
    const labelRejeitado = `\n[REJEITADO] Por ${gestorUser?.name || "Gestor"}: ${motivoRejeicao}`;
    return await pb.collection("estoque_lotes").update(loteId, {
      status: "cancelado",
      observacoes: (lote.observacoes || "") + labelRejeitado,
    });
  },

  // --- 7. EDITAR ITENS PLANEJADOS DO LOTE (Antes de aprovar) ---
  async updateDraftItens(loteId, itens, observacoes = "") {
    return await pb.collection("estoque_lotes").update(loteId, {
      itens_planejados: itens,
      observacoes: observacoes,
    });
  },

  // --- 8. EXCLUIR LOTE RASCUNHO ---
  async deleteDraftLote(loteId) {
    const lote = await pb.collection("estoque_lotes").getOne(loteId);
    if (lote.status !== "rascunho") {
      throw new Error("Apenas lotes em Rascunho podem ser excluídos permanentemente.");
    }
    return await pb.collection("estoque_lotes").delete(loteId);
  },

  // --- 9. SUSPENDER LOTE INTEIRO ---
  async suspendLote(loteId, motivoId, observacoes, user) {
    const lote = await pb.collection("estoque_lotes").getOne(loteId);

    // 1. Atualiza o status do lote para "suspenso" e registra a observação
    const labelSuspenso = `\n[SUSPENSO em ${new Date().toLocaleDateString("pt-BR")}] Por ${user?.name || "Gestor"}. Motivo: ${motivoId}. Obs: ${observacoes || ""}`;
    const updatedLote = await pb.collection("estoque_lotes").update(loteId, {
      status: "suspenso",
      observacoes: (lote.observacoes || "") + labelSuspenso,
    });

    // 2. Busca todas as peças físicas ativas deste lote e suspende-as
    const activeItems = await pb.collection("inventory_items").getFullList({
      filter: `lote = "${loteId}" && isPE = true && situacao_fluxo = "ativo"`
    });

    for (const item of activeItems) {
      await stockProductionService.suspendItem(item.id, motivoId, `Suspensão em Massa do Lote. ${observacoes || ""}`, user?.name || "Gestor");
    }

    return updatedLote;
  },

  // --- 10. REATIVAR LOTE INTEIRO ---
  async reactivateLote(loteId, user) {
    const lote = await pb.collection("estoque_lotes").getOne(loteId);

    // 1. Atualiza o status do lote para "em_producao" e registra a observação
    const labelReativado = `\n[REATIVADO em ${new Date().toLocaleDateString("pt-BR")}] Por ${user?.name || "Gestor"}`;
    const updatedLote = await pb.collection("estoque_lotes").update(loteId, {
      status: "em_producao",
      observacoes: (lote.observacoes || "") + labelReativado,
    });

    // 2. Busca todas as peças físicas suspensas deste lote e reativa-as
    const suspendedItems = await pb.collection("inventory_items").getFullList({
      filter: `lote = "${loteId}" && isPE = true && situacao_fluxo = "suspenso"`
    });

    for (const item of suspendedItems) {
      await stockProductionService.reactivateItem(item.id, user?.name || "Gestor");
    }

    return updatedLote;
  }
};
